import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";

import BudgetBar from "./BudgetBar";
import {
  BudgetDraft, OptionsTree, PreviewResponse, STATUS_COLOR, ScopeLine, categoriesOf, customersOf, dayLabel,
  fmtBudget, fmtUsd, monthName, organizationsOf, parseThreshold, previewHint, prorationHint, scopeIsComplete,
  servicesOf, validateDraft,
} from "./budgets";

const PREVIEW_DELAY_MS = 500;
const emptyLine = (): ScopeLine => ({ organization: "", customer: "", categories: [], services: [] });

interface Props {
  open: boolean;
  mode: "new" | "edit";
  initial: BudgetDraft;
  options: OptionsTree | null;
  loadPreview: (scope: ScopeLine[], amount: number, startDate: string) => Promise<PreviewResponse>;
  onSave: (draft: BudgetDraft) => Promise<void>;
  onClose: () => void;
}

function ScopeLineRow({ line, options, onChange, onRemove }: {
  line: ScopeLine; options: OptionsTree | null; onChange: (line: ScopeLine) => void; onRemove?: () => void;
}) {
  const organizations = organizationsOf(options, line.organization);
  const customers = customersOf(options, line.organization, line.customer);
  const categories = categoriesOf(options, line.organization, line.customer, line.categories);
  const services = servicesOf(options, line.organization, line.customer, line.categories, line.services);
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <TextField select label="Organization" size="small" value={line.organization} sx={{ width: 190 }}
        onChange={(e) => onChange({ organization: e.target.value, customer: "", categories: [], services: [] })}>
        {organizations.map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
      </TextField>
      <Autocomplete size="small" options={customers} value={line.customer || null} disabled={!line.organization}
        onChange={(_, v) => onChange({ ...line, customer: v ?? "", categories: [], services: [] })}
        renderInput={(p) => <TextField {...p} label="Customer" />} sx={{ flex: 1, minWidth: 180 }} />
      <Stack spacing={1} sx={{ width: 240 }}>
        <Autocomplete multiple size="small" options={categories} value={line.categories} disabled={!line.customer}
          onChange={(_, v) => onChange({ ...line, categories: v, services: [] })}
          renderInput={(p) => <TextField {...p} label="Categories" placeholder={line.categories.length ? "" : "All"} />} />
        {line.categories.length > 0 && (
          <Autocomplete multiple size="small" options={services} value={line.services}
            onChange={(_, v) => onChange({ ...line, services: v })}
            renderInput={(p) => <TextField {...p} label="Services" placeholder={line.services.length ? "" : "All"} />} />
        )}
      </Stack>
      <IconButton aria-label="Remove line" size="small" onClick={onRemove} disabled={!onRemove} sx={{ mt: 0.5 }}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
}

function PreviewBox({ preview, amount, thresholds }: { preview: PreviewResponse | null; amount: number; thresholds: number[] }) {
  const lowest = thresholds.length ? Math.min(...thresholds) : 100;
  const hint = preview ? previewHint(preview, thresholds) : null;
  return (
    <Box sx={{ flex: 1, minWidth: 240, alignSelf: "flex-start", bgcolor: "#f8fafc", border: "1px solid #eef0f3", borderRadius: 2, p: 2 }}>
      <Typography variant="subtitle2">How this scope compares</Typography>
      {!preview ? (
        <Typography variant="caption" color="text.secondary">
          Pick customers and an amount to see what they cost in recent months.
        </Typography>
      ) : (
        <Stack spacing={1.5} sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            What the customers above cost, against {fmtBudget(amount)} a month:
          </Typography>
          {preview.months.map((m, i) => {
            const current = i === preview.months.length - 1;
            const pct = m.pct ?? 0;
            const color = current ? "#7c9cc9" : STATUS_COLOR[pct >= 100 ? "red" : pct >= lowest ? "amber" : "green"];
            return (
              <Box key={m.month}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">{monthName(m.month)}{current ? " so far" : ""}</Typography>
                  <Typography variant="body2"><b>{fmtUsd(m.spend_usd)}</b>{current ? "" : ` · ${Math.round(pct)}%`}</Typography>
                </Stack>
                <BudgetBar pct={pct} color={color} />
                {current && <Typography variant="caption" color="text.secondary">through {dayLabel(preview.data_through)}</Typography>}
              </Box>
            );
          })}
          {hint && (
            <Typography variant="caption" color="text.secondary" sx={{ borderTop: "1px solid #e5e7eb", pt: 1 }}>
              {hint}
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}

export default function BudgetDialog({ open, mode, initial, options, loadPreview, onSave, onClose }: Props) {
  const [draft, setDraft] = useState<BudgetDraft>(initial);
  const [thresholdText, setThresholdText] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);

  useEffect(() => {
    if (open) { setDraft(initial); setError(null); setPreview(null); setThresholdText(""); }
  }, [open, initial]);

  const amount = Number(draft.amount);
  const previewKey = JSON.stringify([draft.scope, draft.amount, draft.start_date]);
  useEffect(() => {
    if (!open || !scopeIsComplete(draft.scope) || !(amount > 0)) { setPreview(null); return; }
    let cancelled = false;
    const timer = setTimeout(() => {
      loadPreview(draft.scope, amount, draft.start_date)
        .then((p) => { if (!cancelled) setPreview(p); })
        .catch(() => { if (!cancelled) setPreview(null); });
    }, PREVIEW_DELAY_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, previewKey]);

  const setLine = (index: number, line: ScopeLine) =>
    setDraft((d) => ({ ...d, scope: d.scope.map((l, i) => (i === index ? line : l)) }));
  const removeLine = (index: number) => setDraft((d) => ({ ...d, scope: d.scope.filter((_, i) => i !== index) }));

  const addThreshold = () => {
    const value = parseThreshold(thresholdText);
    if (value === null) { setError("An alert threshold is a whole number from 1 to 500."); return; }
    setDraft((d) => ({ ...d, thresholds: Array.from(new Set([...d.thresholds, value])).sort((a, b) => a - b) }));
    setThresholdText("");
    setError(null);
  };

  const save = async () => {
    const problem = validateDraft(draft);
    if (problem) { setError(problem); return; }
    setSaving(true);
    setError(null);
    try { await onSave(draft); } catch (e) { setError((e as Error).message); } finally { setSaving(false); }
  };

  const hint = prorationHint(amount, draft.start_date);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{mode === "new" ? "New budget" : `Edit ${initial.name}`}</DialogTitle>
      <DialogContent>
        <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ pt: 1 }}>
          <Stack spacing={2} sx={{ flex: 1.6, minWidth: 0 }}>
            <Stack direction="row" spacing={2}>
              <TextField label="Name" size="small" value={draft.name} autoFocus sx={{ flex: 1 }}
                inputProps={{ maxLength: 80 }} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              <TextField label="Note (optional)" size="small" value={draft.note} sx={{ flex: 1.4 }}
                inputProps={{ maxLength: 200 }} onChange={(e) => setDraft({ ...draft, note: e.target.value })} />
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField label="Amount per month" size="small" value={draft.amount} sx={{ width: 170 }}
                inputProps={{ inputMode: "decimal" }}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                onChange={(e) => setDraft({ ...draft, amount: e.target.value })} />
              <TextField label="Start date" type="date" size="small" value={draft.start_date} sx={{ width: 170 }}
                InputLabelProps={{ shrink: true }} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} />
              {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
            </Stack>

            <Typography variant="overline">What counts</Typography>
            {draft.scope.map((line, index) => (
              <ScopeLineRow key={index} line={line} options={options} onChange={(l) => setLine(index, l)}
                onRemove={draft.scope.length > 1 ? () => removeLine(index) : undefined} />
            ))}
            <Box>
              <Button size="small" onClick={() => setDraft((d) => ({ ...d, scope: [...d.scope, emptyLine()] }))}>
                + Add customer
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Pick categories to count only those; under a category you can narrow further to single services.
              Lists show what had costs in the last 90 days.
            </Typography>

            <Typography variant="overline">Alerts</Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {draft.thresholds.map((t) => (
                <Chip key={t} label={`${t} %`} onDelete={draft.thresholds.length > 1
                  ? () => setDraft((d) => ({ ...d, thresholds: d.thresholds.filter((x) => x !== t) })) : undefined} />
              ))}
              <TextField size="small" placeholder="+ add" value={thresholdText} sx={{ width: 90 }}
                inputProps={{ "aria-label": "Add alert threshold in percent", inputMode: "numeric" }}
                onChange={(e) => setThresholdText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addThreshold(); } }} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Followers get one email when this month's spend reaches each of these.
            </Typography>

            {mode === "new" && (
              <FormControlLabel label="Follow this budget (alerts and Monday digest to your email)"
                control={<Checkbox checked={draft.follow} onChange={(e) => setDraft({ ...draft, follow: e.target.checked })} />} />
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
          <PreviewBox preview={preview} amount={amount} thresholds={draft.thresholds} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>{mode === "new" ? "Create budget" : "Save"}</Button>
      </DialogActions>
    </Dialog>
  );
}
